import { useCallback, useEffect, useRef } from 'react';

import type {
  ApiEventId,
  ApiInternshipId,
  ApiJobId,
  ApiOrgId,
  ApiVolopId,
} from 'api/types/ApiTypedId';
import {
  useActionsWhileLoggedOutProcessedAt,
  useLoggedIn,
} from 'contexts/UserEnvironmentContext';
import { useKeyedState } from 'hooks/useKeyedState';
import { useLatest } from 'hooks/useLatest';
import type { SearchType } from 'modules/search/types/SearchType';
import type { SearchSavedListingsStore } from 'modules/search/zustand-stores/searchSavedListingsStore';
import { userDashboardApiClientFetchListingExtras } from 'modules/userDashboard/api/listingExtras/userDashboardApiFetchListingExtras';
import { userDashboardApiCreateSavedListing } from 'modules/userDashboard/api/savedListing/userDashboardApiCreateSavedListing';
import { userDashboardApiDeleteSavedListing } from 'modules/userDashboard/api/savedListing/userDashboardApiDeleteSavedListing';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { LISTING_TYPE_HUMAN_NAME_MAP } from 'utils/constants/general/listingTypeHumanNameMap';
import { deferredWithRouteLoadingAnimation } from 'utils/ui/routeLoadingAnimation';
import { showModal } from 'zustand-stores/modalStore';

export type SaveListingType = (
  e?: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
) => Promise<void>;

type UseSaveListingArgs = {
  listingType: SearchType | 'IMPORTED';
  listingName: string;
  listingId:
    | ApiJobId
    | ApiInternshipId
    | ApiVolopId
    | ApiEventId
    | ApiOrgId
    | null;
  initiallySaved: boolean;
  orgId: ApiOrgId | null;
  orgType: string | null;
  onSaved: (() => void) | null;
  onUnsaved: (() => void) | null;
  trackClickEvent: boolean;
  /**
   * This hooks syncs save/unsave to and from the store
   */
  searchSavedListingsStore: SearchSavedListingsStore | null;
};

export function useSaveListing({
  listingType,
  listingName,
  listingId,
  initiallySaved,
  orgId,
  orgType,
  onSaved,
  onUnsaved,
  trackClickEvent,
  searchSavedListingsStore,
}: UseSaveListingArgs) {
  const { loggedIn } = useLoggedIn();
  const initiallyLoggedInRef = useRef(loggedIn);

  const { actionsWhileLoggedOutProcessedAt } =
    useActionsWhileLoggedOutProcessedAt();
  const previousActionsWhileLoggedOutProcessedAt = useRef(
    actionsWhileLoggedOutProcessedAt,
  );

  const [saved, setSaved] = useKeyedState(initiallySaved, {
    key: listingId ?? '',
  });
  const latestSavedRef = useLatest(saved);

  const saveAsGuest = useCallback(() => {
    if (!listingId) return; // Skip if listingId is null

    showModal('EMAIL_SIGNUP', {
      title: `Save this ${LISTING_TYPE_HUMAN_NAME_MAP[listingType]}`,
      savedListing: {
        id: listingId,
        type: listingType,
        name: listingName,
      },
      redirectTo: `${window.location.pathname}${window.location.search}`,
    });
  }, [listingId, listingName, listingType]);

  const save: SaveListingType = useCallback(async () => {
    if (!listingId) return; // Skip if listingId is null

    if (trackClickEvent) {
      trackClicked('Save Listing', {
        listing_type: LISTING_TYPE_HUMAN_NAME_MAP[listingType],
        listing_id: listingId,
        org_id: orgId,
        org_type: orgType,
      });
    }

    if (!loggedIn) return saveAsGuest();

    await userDashboardApiCreateSavedListing(listingId, listingType);
    onSaved?.();
    setSaved(true);
  }, [
    listingId,
    trackClickEvent,
    loggedIn,
    saveAsGuest,
    listingType,
    onSaved,
    setSaved,
    orgId,
    orgType,
  ]);

  const unsave = useCallback(async () => {
    if (!listingId) return; // Skip if listingId is null
    await userDashboardApiDeleteSavedListing(listingId, listingType);
    onUnsaved?.();
    setSaved(false);
  }, [listingId, listingType, onUnsaved, setSaved]);

  useEffect(() => {
    if (!listingId) return; // Skip if listingId is null

    // The store will fetch the data, so skip this
    if (searchSavedListingsStore) return;

    if (
      initiallyLoggedInRef.current !== loggedIn ||
      previousActionsWhileLoggedOutProcessedAt.current !==
        actionsWhileLoggedOutProcessedAt
    ) {
      void userDashboardApiClientFetchListingExtras([listingId]).then(
        (extras) => {
          const updatedSaved = Boolean(extras[listingId]);
          if (latestSavedRef.current !== updatedSaved) setSaved(updatedSaved);
          return;
        },
      );
    }
  }, [
    loggedIn,
    actionsWhileLoggedOutProcessedAt,
    searchSavedListingsStore,
    listingId,
    latestSavedRef,
    setSaved,
  ]);

  useEffect(() => {
    if (!listingId) return; // Skip if listingId is null
    if (!searchSavedListingsStore) return;

    // Sync when this hook saved
    if (saved !== Boolean(searchSavedListingsStore.getState()[listingId])) {
      searchSavedListingsStore.setState((current) => ({
        ...current,
        [listingId]: saved,
      }));
    }
  }, [latestSavedRef, listingId, saved, searchSavedListingsStore]);

  useEffect(() => {
    if (!listingId) return; // Skip if listingId is null
    if (!searchSavedListingsStore) return;

    // Sync when saved comes from store
    return searchSavedListingsStore.subscribe((newState) => {
      if (Boolean(newState[listingId]) !== latestSavedRef.current) {
        setSaved(Boolean(newState[listingId]));
      }
    });
  }, [listingId, latestSavedRef, setSaved, searchSavedListingsStore]);

  return {
    saved: saved,
    save: deferredWithRouteLoadingAnimation(save),
    unsave: deferredWithRouteLoadingAnimation(unsave),
  };
}
